import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { getLoginUrl } from "@/const";
import { toast } from "sonner";
import { Zap, Crown, Users, Check, Star, Shield, TrendingUp, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type Tier = "founding_monthly" | "founding_lifetime" | "trainer";

export default function Pricing() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { tier: currentTier, isPaid, isTrainer } = useSubscription();
  const [loadingTier, setLoadingTier] = useState<Tier | null>(null);
  const { data: foundingCount } = trpc.stripe.getFoundingCount.useQuery();
  const seatsRemaining = foundingCount?.remaining ?? null;
  const isFull = seatsRemaining !== null && seatsRemaining <= 0;

  const createCheckout = trpc.stripe.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        toast.info("Redirecting to secure checkout...");
        window.open(data.url, "_blank");
      }
      setLoadingTier(null);
    },
    onError: (err) => {
      toast.error(`Checkout failed: ${err.message}`);
      setLoadingTier(null);
    },
  });

  // Handle success/cancel redirects from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      const tier = params.get("tier");
      toast.success(`🎉 Welcome to Forge! Your ${tier?.replace(/_/g, " ")} plan is now active.`);
      window.history.replaceState({}, "", "/pricing");
    }
    if (params.get("canceled") === "true") {
      toast.info("Checkout canceled — no charge was made.");
      window.history.replaceState({}, "", "/pricing");
    }
  }, []);

  const handleCheckout = (tier: Tier) => {
    if (!isAuthenticated) {
      window.location.href = getLoginUrl();
      return;
    }
    setLoadingTier(tier);
    createCheckout.mutate({ tier });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
        >
          <Dumbbell className="w-5 h-5 text-emerald-400" />
          <span className="font-mono font-bold tracking-widest text-emerald-400">FORGE // TELEMETRY</span>
        </button>
        {isAuthenticated ? (
          <button
            onClick={() => navigate("/")}
            className="text-sm text-white/60 hover:text-white transition-colors"
          >
            ← Back to app
          </button>
        ) : (
          <a
            href={getLoginUrl()}
            className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            Sign in
          </a>
        )}
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-16 pb-10 text-center">
        <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30 font-mono text-xs tracking-widest">
          BETA LAUNCH
        </Badge>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
          Train Smarter.<br />
          <span className="text-emerald-400">Pay Less. Lock In Forever.</span>
        </h1>
        <p className="text-white/60 text-lg max-w-xl mx-auto">
          Forge // Telemetry is built for serious lifters. Early supporters get the lowest rate we'll ever offer — locked in for life.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-5xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-3 gap-6">

          {/* Free Tier */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 flex flex-col">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Dumbbell className="w-5 h-5 text-white/40" />
                <span className="font-mono text-xs tracking-widest text-white/40 uppercase">Free</span>
              </div>
              <div className="text-3xl font-black mb-1">$0</div>
              <div className="text-white/40 text-sm">Forever free</div>
            </div>
            <ul className="space-y-3 flex-1 mb-6">
              {[
                "Unlimited workout logging",
                "Exercise library",
                "Basic progress tracking",
                "Plate calculator",
                "Rest timer",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white/60">
                  <Check className="w-4 h-4 text-white/30 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full border-white/20 text-white/60 hover:text-white"
              onClick={() => isAuthenticated ? navigate("/") : (window.location.href = getLoginUrl())}
            >
              {isAuthenticated ? (isPaid ? "Upgrade from Free" : "Current plan") : "Get started free"}
            </Button>
          </div>

          {/* Founding Member */}
          <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-500/5 p-6 flex flex-col relative overflow-hidden">
            {/* Glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 to-transparent pointer-events-none" />

            <div className="relative mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-emerald-400" />
                  <span className="font-mono text-xs tracking-widest text-emerald-400 uppercase">Founding Member</span>
                </div>
                <Badge className={`text-xs font-mono ${isFull ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-orange-500/20 text-orange-400 border-orange-500/30'}`}>
                  {seatsRemaining === null ? 'FIRST 50' : isFull ? 'SOLD OUT' : `${seatsRemaining} LEFT`}
                </Badge>
              </div>
              <div className="flex items-end gap-3 mb-1">
                <div className="text-3xl font-black text-emerald-400">$2.99</div>
                <div className="text-white/40 text-sm mb-1">/month</div>
              </div>
              <div className="text-white/40 text-sm">or <span className="text-white font-bold">$49</span> lifetime — one payment, forever</div>
            </div>

            <ul className="space-y-3 flex-1 mb-6 relative">
              {[
                "Everything in Free",
                "Advanced analytics & PR tracking",
                "4-Week mesocycle planner",
                "AI macro scanner (FUEL tab)",
                "Voice logging",
                "Calendar day stats modal",
                "Early access to all new features",
                "Locked-in rate forever",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <div className="relative space-y-2">
              <Button
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold disabled:opacity-50"
                onClick={() => handleCheckout("founding_monthly")}
                disabled={loadingTier !== null || currentTier === "founding_monthly" || currentTier === "founding_lifetime" || isFull}
              >
                {currentTier === "founding_monthly" ? "✓ Current plan" : currentTier === "founding_lifetime" ? "✓ Lifetime member" : isFull ? "Tier Full" : loadingTier === "founding_monthly" ? "Opening checkout..." : "Start for $2.99/mo"}
              </Button>
              <Button
                variant="outline"
                className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                onClick={() => handleCheckout("founding_lifetime")}
                disabled={loadingTier !== null || currentTier === "founding_lifetime" || isFull}
              >
                {currentTier === "founding_lifetime" ? "✓ You have Lifetime" : isFull ? "Tier Full" : loadingTier === "founding_lifetime" ? "Opening checkout..." : "⚡ Get Lifetime for $49"}
              </Button>
            </div>
          </div>

          {/* Trainer Command Center */}
          <div className="rounded-2xl border border-orange-500/40 bg-orange-500/5 p-6 flex flex-col relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 to-transparent pointer-events-none" />

            <div className="relative mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-orange-400" />
                <span className="font-mono text-xs tracking-widest text-orange-400 uppercase">Trainer</span>
              </div>
              <div className="flex items-end gap-3 mb-1">
                <div className="text-3xl font-black text-orange-400">$9.99</div>
                <div className="text-white/40 text-sm mb-1">/month</div>
              </div>
              <div className="text-white/40 text-sm">Everything you need to run clients</div>
            </div>

            <ul className="space-y-3 flex-1 mb-6 relative">
              {[
                "Everything in Founding Member",
                "Client roster management",
                "Remote workout assignment",
                "Client analytics dashboard",
                "Coaching notes per client",
                "Client availability scheduling",
                "Priority support",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                  <Check className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <Button
              className="w-full bg-orange-500 hover:bg-orange-400 text-black font-bold relative"
              onClick={() => handleCheckout("trainer")}
              disabled={loadingTier !== null || isTrainer}
            >
              {isTrainer ? "✓ Current plan" : loadingTier === "trainer" ? "Opening checkout..." : "Start Trainer Plan"}
            </Button>
          </div>
        </div>

        {/* Trust signals */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            <div className="text-sm font-semibold">Secure Checkout</div>
            <div className="text-xs text-white/40">Powered by Stripe. We never see your card.</div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Zap className="w-6 h-6 text-orange-400" />
            <div className="text-sm font-semibold">Cancel Anytime</div>
            <div className="text-xs text-white/40">Monthly plans cancel in one click. No hassle.</div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-400" />
            <div className="text-sm font-semibold">Price Lock Guarantee</div>
            <div className="text-xs text-white/40">Founding members keep $2.99/mo forever as we grow.</div>
          </div>
        </div>

        {/* Test card note (dev only) */}
        {import.meta.env.DEV && (
          <div className="mt-8 p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10 text-center">
            <p className="text-yellow-400 text-xs font-mono">
              DEV MODE — Use test card: <strong>4242 4242 4242 4242</strong> · Any future date · Any CVC
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
